import {Injectable} from "@angular/core";
import {of, Subject} from "rxjs";
import {Backside, Color, Lens, LensDetails, LensType, Material} from "../model/catalog/catalog.model";
import {LensConfigMode, LensSectionMode, LensSide} from "../model/configuration/configuration";
import {LensOrderItem} from "../model/order/order";
import {ResponseViewComponent} from "./new-order-proposal/response-view/response-view.component";
import {CalculationComponent} from "./new-order-trial-lens-with-overrefarction/calculation/calculation.component";
import {NewParameterComponent} from "./new-order-trial-lens-with-overrefarction/new-parameter/new-parameter.component";
import {OverRefractionComponent} from "./new-order-trial-lens-with-overrefarction/over-refraction/over-refraction.component";
import {TrialLensComponent} from "./new-order-trial-lens-with-overrefarction/trial-lens/trial-lens.component";
import {CatalogApiService} from "../api/catalog-api.service";
import {switchMap} from "rxjs/operators";
import {ManufactureApiService} from "../api/manufacture-api.service";
import {ProductionDate} from "../model/manufacture/date";

@Injectable()
export class NewOrderStoreService {

  lensConfigMode$: Subject<LensConfigMode> = new Subject<LensConfigMode>();
  lensConfigMode: LensConfigMode = LensConfigMode.DUAL;

  isRightSideEnable$: Subject<boolean> = new Subject<boolean>();
  isRightSideEnable: boolean = true;
  isLeftSideEnable$: Subject<boolean> = new Subject<boolean>();
  isLeftSideEnable: boolean = true;

  radiusBasecurveTrialLensLeft: Object;
  radiusBasecurveTrialLensLeft$: Subject<Object> = new Subject<Object>();
  radiusBasecurve2TrialLensLeft: Object;
  radiusBasecurve2TrialLensLeft$: Subject<Object> = new Subject<Object>();
  materialTrialLensLeft: Object;
  materialTrialLensLeft$: Subject<Object> = new Subject<Object>();

  radiusBasecurveTrialLensRight: Object;
  radiusBasecurveTrialLensRight$: Subject<Object> = new Subject<Object>();
  radiusBasecurve2TrialLensRight: Object;
  radiusBasecurve2TrialLensRight$: Subject<Object> = new Subject<Object>();
  materialTrialLensRight: Object;
  materialTrialLensRight$: Subject<Object> = new Subject<Object>();

  responseProposalRight$: Subject<any> = new Subject<any>();
  responseProposalLeft$: Subject<any> = new Subject<any>();

  trialLensRight$: Subject<TrialLensComponent> = new Subject<TrialLensComponent>();
  overRefractionTrialLensRight$: Subject<OverRefractionComponent> = new Subject<OverRefractionComponent>();
  newParameterTrialLensRight$: Subject<NewParameterComponent> = new Subject<NewParameterComponent>();
  calculationTrialLensRight$: Subject<CalculationComponent> = new Subject<CalculationComponent>();
  trialLensAllRight$: Subject<any> = new Subject<any>();

  trialLensLeft$: Subject<TrialLensComponent> = new Subject<TrialLensComponent>();
  overRefractionTrialLensLeft$: Subject<OverRefractionComponent> = new Subject<OverRefractionComponent>();
  newParameterTrialLensLeft$: Subject<NewParameterComponent> = new Subject<NewParameterComponent>();
  calculationTrialLensLeft$: Subject<CalculationComponent> = new Subject<CalculationComponent>();
  trialLensAllLeft$: Subject<any> = new Subject<any>();

  copyFinalLensRight: LensOrderItem;
  copyFinalLensRight$: Subject<LensOrderItem> = new Subject<LensOrderItem>();
  copyFinalLensLeft: LensOrderItem;
  copyFinalLensLeft$: Subject<LensOrderItem> = new Subject<LensOrderItem>();

  editFinalLensRight: LensOrderItem;
  editFinalLensRight$: Subject<Object> = new Subject<Object>();
  editFinalLensLeft: LensOrderItem;
  editFinalLensLeft$: Subject<Object> = new Subject<Object>();

  copyTrialLensRight: LensOrderItem;
  copyTrialLensLeft: LensOrderItem;
  copyTrialLensRight$: Subject<LensOrderItem> = new Subject<LensOrderItem>();
  copyTrialLensLeft$: Subject<LensOrderItem> = new Subject<LensOrderItem>();

  commissionFinalLens: string;
  commissionFinalLens$: Subject<string> = new Subject<string>();

  lensSectionMode$: Subject<LensSectionMode> = new Subject<LensSectionMode>();
  lensSectionMode: LensSectionMode;

  sendOrder$: Subject<boolean> = new Subject<boolean>();

  finishedLoading: boolean = false;
  backSides: Backside[] = [];
  families: Lens[] = []
  materials: Material[] = [];
  colors: Color[] = [];
  details: LensDetails[] = [];
  offDays: ProductionDate[] = [];

  constructor(private catalog: CatalogApiService) {
    let start = performance.now();
    let end = performance.now();
    // preload cached static data
    this.catalog.preload().subscribe((data) => {
      this.backSides = data.backSides;
      this.families = data.families;
      // filter outdated material
      this.materials = data.materials.filter(m => m.name != 'Bo-EO-UV');
      this.colors = data.colors;
      this.details = data.details;
      this.offDays = data.offDays;
      end = performance.now();
      console.log('preload massive data', end - start);
      this.finishedLoading = true;
    });

    this.lensConfigMode$.subscribe((value) => {
      this.lensConfigMode = value;
    });

    this.lensSectionMode$.subscribe((value) => {
      this.lensSectionMode = value;
    });

    this.isRightSideEnable$.subscribe((value) => {
      this.isRightSideEnable = value;
    });

    this.isLeftSideEnable$.subscribe((value) => {
      this.isLeftSideEnable = value;
    });

    this.radiusBasecurveTrialLensRight$.subscribe((value) => {
      this.radiusBasecurveTrialLensRight = value;
    });

    this.radiusBasecurve2TrialLensRight$.subscribe((value) => {
      this.radiusBasecurve2TrialLensRight = value;
    });

    this.materialTrialLensRight$.subscribe((value) => {
      this.materialTrialLensRight = value;
    });

    this.radiusBasecurveTrialLensLeft$.subscribe((value) => {
      this.radiusBasecurveTrialLensLeft = value;
    });

    this.radiusBasecurve2TrialLensLeft$.subscribe((value) => {
      this.radiusBasecurve2TrialLensLeft = value;
    });

    this.materialTrialLensLeft$.subscribe((value) => {
      this.materialTrialLensLeft = value;
    });

    this.commissionFinalLens$.subscribe((value) => {
      this.commissionFinalLens = value;
    })
  }

  public setLensConfigMode(mode: LensConfigMode) {
    this.lensConfigMode = mode;
  }

  public getLensConfigMode(): LensConfigMode {
    return this.lensConfigMode;
  }

  public toggleLensConfigMode() {
    if (this.lensConfigMode === LensConfigMode.DUAL || ((this.isRightSideEnable && !this.isLeftSideEnable) || (this.isLeftSideEnable && !this.isRightSideEnable))) {
      this.lensConfigMode$.next(LensConfigMode.SINGLE);
    } else if (this.isRightSideEnable && this.isLeftSideEnable) {
      this.lensConfigMode$.next(LensConfigMode.DUAL);
    }
  }

  public changeLensConfigMode(lensConfigMode: LensConfigMode) {
    this.lensConfigMode$.next(lensConfigMode);
    this.setLensConfigMode(lensConfigMode);
  }

  public changeLensSectionMode(lensSectionMode: LensSectionMode) {
    this.lensSectionMode$.next(lensSectionMode);
    this.setLensSectionMode(lensSectionMode);
  }

  public setLensSectionMode(mode: LensSectionMode) {
    this.lensSectionMode = mode;
  }

  public getLensSectionMode(): LensSectionMode {
    return this.lensSectionMode;
  }

  public setIsRightSideEnable(isEnable: boolean) {
    this.isRightSideEnable = isEnable;
  }

  public getIsRightSideEnable(): boolean {
    return this.isRightSideEnable;
  }

  public toggleIsRightSideEnable(isSwitch = false) {
    if (this.isRightSideEnable) {
      this.isRightSideEnable$.next(false);
      if (!isSwitch) {
        this.isLeftSideEnable$.next(true);
      }
    } else {
      this.isRightSideEnable$.next(true);
    }
  }

  public changeIsRightSideEnable(isRightSideEnable: boolean) {
    this.isRightSideEnable$.next(isRightSideEnable);
    this.setIsRightSideEnable(isRightSideEnable);
  }

  public setIsLeftSideEnable(isEnable: boolean) {
    this.isLeftSideEnable = isEnable;
  }

  public getIsLeftSideEnable(): boolean {
    return this.isLeftSideEnable;
  }

  public toggleIsLeftSideEnable(isSwitch = false) {
    if (this.isLeftSideEnable) {
      this.isLeftSideEnable$.next(false);
      if (!isSwitch) {
        this.isRightSideEnable$.next(true);
      }
    } else {
      this.isLeftSideEnable$.next(true);
    }
  }

  public changeIsLeftSideEnable(isLeftSideEnable: boolean) {
    this.isLeftSideEnable$.next(isLeftSideEnable);
    this.setIsLeftSideEnable(isLeftSideEnable);
  }

  public editLensSideEnable() {
    if (this.isRightSideEnable) {
      this.isRightSideEnable$.next(true);
      this.isLeftSideEnable$.next(false);
    } else if (this.isLeftSideEnable) {
      this.isRightSideEnable$.next(false);
      this.isLeftSideEnable$.next(true);
    }
  }

  public setRadiusBasecurveTrialLensLeft(radiusBasecurveTrialLens: number) {
    this.radiusBasecurveTrialLensLeft$.next(radiusBasecurveTrialLens);
  }

  public setRadiusBasecurve2TrialLensLeft(radiusBasecurve2TrialLens: number) {
    this.radiusBasecurve2TrialLensLeft$.next(radiusBasecurve2TrialLens);
  }

  public setMaterialTrialLensLeft(materialTrialLens: number) {
    this.materialTrialLensLeft$.next(materialTrialLens);
  }

  public setRadiusBasecurveTrialLensRight(radiusBasecurveTrialLens: number) {
    this.radiusBasecurveTrialLensRight$.next(radiusBasecurveTrialLens);
  }

  public setRadiusBasecurve2TrialLensRight(radiusBasecurve2TrialLens: number) {
    this.radiusBasecurve2TrialLensRight$.next(radiusBasecurve2TrialLens);
  }

  public setMaterialTrialLensRight(materialTrialLens: number) {
    this.materialTrialLensRight$.next(materialTrialLens);
  }

  public setMaterials(materials: Array<Material>) {
    this.materials = materials;
  }

  public getMaterials() {
    return this.materials;
  }

  public setBackSides(backSides: Array<Backside>) {
    this.backSides = backSides;
  }

  public getBackSides() {
    return this.backSides;
  }

  public setResponseProposalRight(responseView: ResponseViewComponent, lensType: LensType) {
    this.responseProposalRight$.next({responseView: responseView, lensType: lensType});
  }

  public setResponseProposalLeft(responseView: ResponseViewComponent, lensType: LensType) {
    this.responseProposalLeft$.next({responseView: responseView, lensType: lensType});
  }

  public setTrialLensAllRight(trialLens: Object) {
    this.trialLensAllRight$.next(trialLens);
  }

  public setTrialLensAllLeft(trialLens: Object) {
    this.trialLensAllLeft$.next(trialLens);
  }

  public setCommissionFinalLens(commission: string) {
    this.commissionFinalLens$.next(commission);
  }

  public setCopyFinalLensRight(copyFinalLens: LensOrderItem) {
    this.copyFinalLensRight = copyFinalLens;
    this.copyFinalLensRight$.next(copyFinalLens);
  }

  public getCopyFinalLensRight() {
    return this.copyFinalLensRight;
  }

  public setCopyFinalLensLeft(copyFinalLens: LensOrderItem) {
    this.copyFinalLensLeft = copyFinalLens;
    this.copyFinalLensLeft$.next(copyFinalLens);
  }

  public getCopyFinalLensLeft() {
    return this.copyFinalLensLeft;
  }

  public setEditFinalLensRight(editFinalLens: LensOrderItem, index: number) {
    this.editFinalLensRight = editFinalLens;
    this.editFinalLensRight$.next({editFinalLens: editFinalLens, index: index});
  }

  public getEditFinalLensRight() {
    return this.editFinalLensRight;
  }

  public setEditFinalLensLeft(editFinalLens: LensOrderItem, index: number) {
    this.editFinalLensLeft = editFinalLens;
    this.editFinalLensLeft$.next({editFinalLens: editFinalLens, index: index});
  }

  public getEditFinalLensLeft() {
    return this.editFinalLensLeft;
  }

  public setCopyTrialLensRight(copyTrialLens: LensOrderItem) {
    this.copyTrialLensRight = copyTrialLens;
    this.copyTrialLensRight$.next(copyTrialLens);
  }

  public getCopyTrialLensRight() {
    return this.copyTrialLensRight;
  }

  public setCopyTrialLensLeft(copyTrialLens: LensOrderItem) {
    this.copyTrialLensLeft = copyTrialLens;
    this.copyTrialLensLeft$.next(copyTrialLens);
  }

  public getCopyTrialLensLeft() {
    return this.copyTrialLensLeft;
  }

  public sendOrder() {
    this.sendOrder$.next(true);
  }
}
